import { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "../../services/firebase";

import MixedCompanyAssignJobs from "./MixedCompanyAssignJobs";
import { getNextDate } from "../../utils/recurrence";

const inputClass =
  "w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 shadow-sm outline-none transition placeholder:text-gray-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100";

const labelClass =
  "text-[11px] font-semibold uppercase tracking-[0.14em] text-gray-500";

export default function AssignJobsTab({
  scriptLoaded,
  drivers = [],
  companies: companiesProp,
}) {
  const [companies, setCompanies] = useState(
    Array.isArray(companiesProp) ? companiesProp : [],
  );
  const [loadingCompanies, setLoadingCompanies] = useState(
    !Array.isArray(companiesProp),
  );

  const [mixedCtx, setMixedCtx] = useState({
    driverUid: "",
    driverEmail: "",
    localDate: "",
    cleanedRuns: [],
    targets: [],
  });

  const [ruleEnabled, setRuleEnabled] = useState(false);
  const [ruleName, setRuleName] = useState("Recurring Run");
  const [ruleFrequency, setRuleFrequency] = useState("fortnightly");
  const [ruleStartDate, setRuleStartDate] = useState(() =>
    new Date().toLocaleDateString("en-CA", { timeZone: "Australia/Melbourne" }),
  );
  const [ruleEndDate, setRuleEndDate] = useState("");
  const [ruleActive, setRuleActive] = useState(true);

  useEffect(() => {
    if (Array.isArray(companiesProp)) {
      const normalized = companiesProp
        .map((company) => {
          const data = company || {};
          const id = String(data.id || "").trim();
          const rawName = data.name || data.companyName || data.displayName || "";
          const name = String(rawName).trim();
          if (!id) return null;
          return {
            ...data,
            id,
            name: name || "Untitled company",
          };
        })
        .filter(Boolean)
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));

      setCompanies(normalized);
      setLoadingCompanies(false);
      return undefined;
    }

    setLoadingCompanies(true);
    const unsub = onSnapshot(collection(db, "companies"), (snap) => {
      const list = snap.docs
        .map((d) => {
          const data = d.data() || {};
          const rawName = data.name || data.companyName || data.displayName || "";
          const name = String(rawName).trim();
          return {
            id: d.id,
            ...data,
            name: name || "Untitled company",
          };
        })
        .filter((company) => company.id)
        .sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
      setCompanies(list);
      setLoadingCompanies(false);
    });
    return () => unsub();
  }, [companiesProp]);

  const driverMap = useMemo(() => {
    const map = {};
    (drivers || []).forEach((d) => (map[d.uid] = d));
    return map;
  }, [drivers]);

  const nextPreview = useMemo(() => {
    if (!ruleEnabled || !ruleStartDate) return "";
    return getNextDate(ruleStartDate, ruleFrequency);
  }, [ruleEnabled, ruleStartDate, ruleFrequency]);

  const runsCount = mixedCtx.cleanedRuns?.length || 0;
  const totalStops = (mixedCtx.cleanedRuns || []).reduce(
    (sum, run) => sum + (run.stops?.length || 0),
    0,
  );

  async function createInitialAssignmentsFromTargets({
    dateStr,
    driverUid,
    driverEmail,
    targets,
    ruleId,
  }) {
    const legacyAssignmentId = `${driverUid}_${dateStr}`;

    const existingLegacy = await getDoc(doc(db, "assignments", legacyAssignmentId));
    if (existingLegacy.exists()) {
      const ok = window.confirm(
        "An assignment already exists for this driver and date.\n\nCreating the first recurring day will overwrite it.\n\nContinue?",
      );
      if (!ok) return { ok: false, reason: "cancelled" };
    }

    const combinedRunsWithIds = {};
    const combinedRunMeta = {};
    const perCompany = {};

    for (const t of targets) {
      const companyId = t.companyId;
      const companyName = t.companyName || "";
      const uiVariant = t.uiVariant || "default";
      const runsTemplate = t.runsTemplate || {};

      if (!perCompany[companyId]) {
        perCompany[companyId] = { runsWithIds: {}, uiVariant, companyName };
      }

      for (const [runKey, stops] of Object.entries(runsTemplate)) {
        if (!combinedRunsWithIds[runKey]) combinedRunsWithIds[runKey] = [];
        if (!combinedRunMeta[runKey]) {
          combinedRunMeta[runKey] = { companyId, companyName, uiVariant };
        }

        if (!perCompany[companyId].runsWithIds[runKey]) {
          perCompany[companyId].runsWithIds[runKey] = [];
        }

        for (const stop of stops || []) {
          const jobPayload = {
            companyId,
            companyName,
            driverId: driverUid,
            driverEmail: driverEmail || "",
            date: dateStr,
            runKey,
            uiVariant,
            address: stop.address || "",
            eta: stop.eta || "",
            description: stop.description || "",
            poNumber: stop.poNumber || "",
            siteContact: stop.siteContact || "",
            sitePhone: stop.sitePhone || "",
            siteEmail: stop.siteEmail || "",
            fixed: !!stop.fixed,
            recurrenceDate: ruleId ? dateStr : "",
            customerId: stop.customerId || "",
            customerName: stop.customerName || "",
            siteId: stop.siteId || "",
            siteLabel: stop.siteLabel || "",
            jobTypeId: stop.jobTypeId || "",
            jobTypeCode: stop.jobTypeCode || "",
            jobTypeName: stop.jobTypeName || "",
            recurrenceRuleId: ruleId || "",
            status: "assigned",
            createdAt: Date.now(),
            updatedAt: Date.now(),
          };

          const ref = await addDoc(collection(db, "jobs"), jobPayload);

          const stopWithId = {
            jobId: ref.id,
            address: jobPayload.address,
            eta: jobPayload.eta,
            description: jobPayload.description,
            poNumber: jobPayload.poNumber,
            siteContact: jobPayload.siteContact,
            sitePhone: jobPayload.sitePhone,
            siteEmail: jobPayload.siteEmail,
            fixed: jobPayload.fixed,
            jobTypeId: jobPayload.jobTypeId,
            jobTypeCode: jobPayload.jobTypeCode,
            jobTypeName: jobPayload.jobTypeName,
            customerId: jobPayload.customerId,
            customerName: jobPayload.customerName,
            siteId: jobPayload.siteId,
            siteLabel: jobPayload.siteLabel,
          };

          combinedRunsWithIds[runKey].push(stopWithId);
          perCompany[companyId].runsWithIds[runKey].push(stopWithId);
        }
      }
    }

    const combinedAssignmentPayload = {
      driverId: driverUid,
      driverEmail: driverEmail || "",
      date: dateStr,
      runs: combinedRunsWithIds,
      runMeta: combinedRunMeta,
      updatedAt: Date.now(),
      recurrenceRuleId: ruleId || "",
      uiVariant: "mixed",
      recurrenceDate: ruleId ? dateStr : "",
    };

    await setDoc(doc(db, "assignments", legacyAssignmentId), combinedAssignmentPayload, {
      merge: false,
    });

    for (const [companyId, data] of Object.entries(perCompany)) {
      const scopedId = `${companyId}_${driverUid}_${dateStr}`;

      await setDoc(
        doc(db, "assignments", scopedId),
        {
          companyId,
          companyName: data.companyName || "",
          driverId: driverUid,
          driverEmail: driverEmail || "",
          date: dateStr,
          runs: data.runsWithIds,
          uiVariant: data.uiVariant || "default",
          updatedAt: Date.now(),
          recurrenceRuleId: ruleId || "",
        },
        { merge: false },
      );
    }

    return { ok: true };
  }

  async function createRecurringRuleFromMixed() {
    const chosenDriver = mixedCtx.driverUid ? driverMap[mixedCtx.driverUid] : null;

    if (!chosenDriver) {
      alert("Please choose a driver in the Assign section first.");
      return;
    }
    if (!ruleStartDate) {
      alert("Please choose a start date.");
      return;
    }
    if (ruleEndDate && ruleEndDate < ruleStartDate) {
      alert("End date must be after the start date.");
      return;
    }
    if (!["daily", "weekly", "fortnightly", "monthly"].includes(ruleFrequency)) {
      alert("Invalid frequency.");
      return;
    }

    const targets = Array.isArray(mixedCtx.targets) ? mixedCtx.targets : [];
    const hasAnyStops = targets.some(
      (t) => t?.runsTemplate && Object.keys(t.runsTemplate).length,
    );
    const missingCompany = (mixedCtx.cleanedRuns || []).some((r) => !r.companyId);

    if (!hasAnyStops) {
      alert("Please add at least one address in the runs before creating a recurring rule.");
      return;
    }
    if (missingCompany) {
      alert("Please choose a company for every run before creating a recurring rule.");
      return;
    }

    try {
      const nextDatePreview = getNextDate(ruleStartDate, ruleFrequency);

      const ruleDoc = {
        name: (ruleName || "Recurring Run").trim(),
        driverId: chosenDriver.uid,
        driverEmail: chosenDriver.email || "",
        startDate: ruleStartDate,
        endDate: ruleEndDate ? ruleEndDate : null,
        frequency: ruleFrequency,
        active: !!ruleActive,
        currentDate: ruleStartDate,
        targets,
        uiVariant: "mixed",
        createdFrom: "mixed-assign",
        nextDatePreview: nextDatePreview || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        endedAt: null,
      };

      const ruleRef = await addDoc(collection(db, "recurrenceRules"), ruleDoc);

      const init = await createInitialAssignmentsFromTargets({
        dateStr: ruleStartDate,
        driverUid: chosenDriver.uid,
        driverEmail: chosenDriver.email || "",
        targets,
        ruleId: ruleRef.id,
      });

      if (!init.ok) return;

      alert(
        `Recurring rule created.\n\nStart: ${ruleStartDate}\nFrequency: ${ruleFrequency}\nNext: ${
          nextDatePreview || "-"
        }\n\nNext occurrence will be auto-created when this day is fully completed/skipped.`,
      );

      setRuleEnabled(true);
    } catch (e) {
      console.error("Failed to create recurring rule:", e);
      alert("Failed to create recurring rule. Please check the console.");
    }
  }

    return (
    <div className="space-y-6 text-gray-900">
      {loadingCompanies ? (
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-4 text-sm text-gray-500 shadow-sm">
          Loading companies…
        </div>
      ) : (
        <MixedCompanyAssignJobs
          companies={companies}
          drivers={drivers}
          scriptLoaded={scriptLoaded}
          onContextChange={setMixedCtx}
        />
      )}

      <div className="overflow-hidden rounded-[28px] border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 bg-gradient-to-r from-emerald-50 via-white to-white px-5 py-5 sm:px-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
                Recurring
              </div>
              <h3 className="mt-1 text-xl font-semibold text-gray-950">
                Create recurring rule from these runs
              </h3>
              <p className="mt-1 max-w-2xl text-sm text-gray-600">
                Use the current mixed runs as a template. The first day is created immediately, and future days are generated automatically as work is completed or skipped.
              </p>
            </div>

            <label className="inline-flex items-center gap-3 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm">
              <input
                type="checkbox"
                checked={ruleEnabled}
                onChange={(e) => setRuleEnabled(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
              />
              Enable recurring
            </label>
          </div>
        </div>

        <div className="px-5 py-5 sm:px-6">
          {!ruleEnabled ? (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-5 text-sm text-gray-500">
              Turn on recurring to name the rule, choose frequency, set dates, and create the first recurring day.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="grid gap-4 xl:grid-cols-6">
                <div className="xl:col-span-2">
                  <div className="flex flex-col gap-1.5">
                    <span className={labelClass}>Rule name</span>
                    <input
                      className={inputClass}
                      value={ruleName}
                      onChange={(e) => setRuleName(e.target.value)}
                      placeholder="Mixed Runs - Weekly"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex flex-col gap-1.5">
                    <span className={labelClass}>Frequency</span>
                    <select
                      className={inputClass}
                      value={ruleFrequency}
                      onChange={(e) => setRuleFrequency(e.target.value)}
                    >
                      <option value="daily">Daily</option>
                      <option value="weekly">Weekly</option>
                      <option value="fortnightly">Fortnightly</option>
                      <option value="monthly">Monthly</option>
                    </select>
                  </div>
                </div>

                <div>
                  <div className="flex flex-col gap-1.5">
                    <span className={labelClass}>Start date</span>
                    <input
                      type="date"
                      className={inputClass}
                      value={ruleStartDate}
                      onChange={(e) => setRuleStartDate(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex flex-col gap-1.5">
                    <span className={labelClass}>End date</span>
                    <input
                      type="date"
                      className={inputClass}
                      value={ruleEndDate || ""}
                      onChange={(e) => setRuleEndDate(e.target.value)}
                    />
                  </div>
                </div>

                <div>
                  <div className="flex h-full items-end">
                    <label className="inline-flex w-full items-center justify-between rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm font-medium text-gray-700">
                      <span>Active</span>
                      <input
                        id="ruleActive"
                        type="checkbox"
                        checked={ruleActive}
                        onChange={(e) => setRuleActive(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                    </label>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-4 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex flex-wrap items-center gap-3 text-sm text-emerald-900">
                  <span className="rounded-full border border-emerald-200 bg-white px-3 py-1 font-medium">
                    Next occurrence: {nextPreview || "-"}
                  </span>
                  <span className="rounded-full border border-emerald-200 bg-white px-3 py-1 font-medium">
                    Driver selected: {mixedCtx?.driverUid ? "Yes" : "No"}
                  </span>
                  <span className="rounded-full border border-emerald-200 bg-white px-3 py-1 font-medium">
                    Runs ready: {runsCount}
                  </span>
                  <span className="rounded-full border border-emerald-200 bg-white px-3 py-1 font-medium">
                    Stops ready: {totalStops}
                  </span>
                </div>

                <button
                  className="inline-flex items-center justify-center rounded-xl bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                  onClick={createRecurringRuleFromMixed}
                  disabled={!mixedCtx?.driverUid}
                >
                  Create recurring rule
                </button>
              </div>

              <div className="text-xs leading-6 text-gray-500">
                This saves the current mixed runs as the template, creates the first assignment day immediately, and lets future occurrences continue from the same pattern.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
